if (!customElements.get('dynamic-comparison-table')) {
  customElements.define(
    'dynamic-comparison-table',
    class DynamicComparisonTable extends HTMLElement {
      constructor() {
        super();
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.selects = [];
      }

      connectedCallback() {
        document.addEventListener('click', this.handleDocumentClick);

        if (this.initialized) return;
        this.initialized = true;

        this.selects = Array.from(this.querySelectorAll('[data-dqct-select]'));
        this.selects.forEach((select) => this.setupSelect(select));
        this.refreshOptionStates();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleDocumentClick);
      }

      setupSelect(select) {
        const toggle = select.querySelector('[data-dqct-toggle]');
        const list = select.querySelector('[data-dqct-list]');

        if (!toggle || !list) return;

        toggle.addEventListener('click', () => {
          if (toggle.getAttribute('aria-expanded') === 'true') {
            this.closeSelect(select);
          } else {
            this.openSelect(select);
          }
        });

        toggle.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            this.openSelect(select);
          } else if (event.key === 'Escape') {
            this.closeSelect(select);
          }
        });

        list.addEventListener('click', (event) => {
          const option = event.target.closest('[data-dqct-option]');
          if (option) this.selectOption(select, option);
        });

        list.addEventListener('keydown', (event) => this.handleListKeydown(select, event));
      }

      handleListKeydown(select, event) {
        const options = this.getOptions(select);
        const currentIndex = options.indexOf(document.activeElement);

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.focusOption(options, currentIndex, 1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.focusOption(options, currentIndex, -1);
            break;
          case 'Home':
            event.preventDefault();
            this.focusOption(options, -1, 1);
            break;
          case 'End':
            event.preventDefault();
            this.focusOption(options, options.length, -1);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (currentIndex > -1) this.selectOption(select, options[currentIndex]);
            break;
          case 'Escape':
            event.preventDefault();
            this.closeSelect(select, true);
            break;
          case 'Tab':
            this.closeSelect(select);
            break;
        }
      }

      focusOption(options, fromIndex, direction) {
        for (let index = fromIndex + direction; index >= 0 && index < options.length; index += direction) {
          if (options[index].getAttribute('aria-disabled') !== 'true') {
            options[index].focus();
            return;
          }
        }
      }

      getOptions(select) {
        return Array.from(select.querySelectorAll('[data-dqct-option]'));
      }

      getSelectedId(select) {
        const selected = select.querySelector('[data-dqct-option][aria-selected="true"]');
        return selected ? selected.dataset.productId : null;
      }

      openSelect(select) {
        this.selects.forEach((other) => {
          if (other !== select) this.closeSelect(other);
        });

        const toggle = select.querySelector('[data-dqct-toggle]');
        const list = select.querySelector('[data-dqct-list]');

        list.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        this.alignList(select, toggle, list);

        const options = this.getOptions(select);
        const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
        const target = selected || options.find((option) => option.getAttribute('aria-disabled') !== 'true');

        if (target) target.focus();
      }

      // The list can be wider than its column, so open it towards the side that is
      // still visible inside the horizontally scrollable table and keep it in view.
      alignList(select, toggle, list) {
        const scroller = this.querySelector('.dqct__scroller');

        list.classList.remove('dqct-select__list--flipped');
        list.style.transform = '';

        if (!scroller) return;

        const bounds = scroller.getBoundingClientRect();
        const anchor = toggle.getBoundingClientRect();
        const listWidth = list.offsetWidth;
        const rtl = getComputedStyle(this).direction === 'rtl';
        const overflows = rtl ? anchor.right - listWidth < bounds.left : anchor.left + listWidth > bounds.right;
        const flippedFits = rtl ? anchor.left + listWidth <= bounds.right : anchor.right - listWidth >= bounds.left;

        if (overflows && flippedFits) list.classList.add('dqct-select__list--flipped');

        const rect = list.getBoundingClientRect();
        const overflowRight = rect.right - bounds.right;
        const overflowLeft = bounds.left - rect.left;
        let shift = 0;

        if (overflowRight > 0) shift = -overflowRight;
        else if (overflowLeft > 0) shift = overflowLeft;

        if (shift) list.style.transform = `translateX(${Math.round(shift)}px)`;
      }

      closeSelect(select, focusToggle) {
        const toggle = select.querySelector('[data-dqct-toggle]');
        const list = select.querySelector('[data-dqct-list]');

        if (!toggle || !list || list.hidden) return;

        list.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');

        if (focusToggle) toggle.focus();
      }

      selectOption(select, option) {
        if (option.getAttribute('aria-disabled') === 'true') return;

        this.getOptions(select).forEach((item) => {
          item.setAttribute('aria-selected', item === option ? 'true' : 'false');
        });

        const label = select.querySelector('[data-dqct-selected-label]');
        if (label) {
          label.textContent = option.textContent.trim();
          label.title = label.textContent;
        }

        this.applyProduct(select, option.dataset.productId);
        this.refreshOptionStates();
        this.closeSelect(select, true);
      }

      applyProduct(select, productId) {
        const card = select.closest('[data-dqct-column]');
        const template = this.querySelector(`template[data-dqct-product="${productId}"]`);

        if (!card || !template) return;

        const column = card.dataset.dqctColumn;
        const fragment = template.content.cloneNode(true);

        const media = fragment.querySelector('[data-dqct-part="media"]');
        const mediaTarget = card.querySelector('[data-dqct-media]');
        if (media && mediaTarget) mediaTarget.replaceChildren(...media.childNodes);

        fragment.querySelectorAll('[data-dqct-part="value"]').forEach((source) => {
          const cell = this.querySelector(
            `[data-dqct-column="${column}"][data-dqct-row="${source.dataset.dqctRow}"]`
          );
          if (cell) cell.replaceChildren(...source.childNodes);
        });
      }

      refreshOptionStates() {
        const selectedIds = this.selects.map((select) => this.getSelectedId(select));

        this.selects.forEach((select, index) => {
          this.getOptions(select).forEach((option) => {
            const takenByOther = selectedIds.some(
              (selectedId, selectIndex) => selectIndex !== index && selectedId === option.dataset.productId
            );

            if (takenByOther) {
              option.setAttribute('aria-disabled', 'true');
            } else {
              option.removeAttribute('aria-disabled');
            }
          });
        });
      }

      handleDocumentClick(event) {
        this.selects.forEach((select) => {
          if (!select.contains(event.target)) this.closeSelect(select);
        });
      }
    }
  );
}
